<template>
  <div :class="cssClass">
    <div v-if="$slots.header" class="c-virtual-list__header">
      <slot name="header"></slot>
    </div>
    <div class="c-virtual-list__content" ref="content">
      <RecycleScroller
        :buffer="buffer"
        :direction="direction"
        :items="items"
        :item-size="itemSize"
        :style="scrollerStyle"
        class="c-virtual-list__scroller"
        ref="scroller"
        @resize="resize"
      >
        <template slot="before">
          <slot name="before"></slot>
        </template>
        <template v-slot="{ item }">
          <slot :item="item"></slot>
        </template>
      </RecycleScroller>
      <div
        v-if="hasScrollbar"
        :class="scrollbarClass"
        @mousedown.self="clickScrollbar"
      >
        <div
          class="c-virtual-list-scrollbar__thumb"
          :style="thumbStyle"
          @mousedown="mousedown"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  props: {
    buffer: {
      type: Number,
      required: false
    },
    direction: {
      type: String,
      required: false,
      default: 'vertical'
    },
    items: {
      type: Array,
      required: true
    },
    itemSize: {
      type: Number,
      required: true
    },
    thumbMinSize: {
      type: Number,
      required: false,
      default: 20
    }
  },
  components: {
    RecycleScroller
  },
  data() {
    return {
      offsetSize: 0,
      scrollbarSize: 0,
      scrollPos: 0,
      scrollSize: 0,
      thumbOffsetSize: 0,
      isDragging: false
    };
  },
  computed: {
    cssClass() {
      return {
        'c-virtual-list': true,
        ['c-virtual-list--' + this.direction]: true
      };
    },
    scrollbarClass() {
      return {
        'c-virtual-list-scrollbar': true,
        ['c-virtual-list-scrollbar--' + this.direction]: true,
        'is-hover': this.isDragging
      };
    },
    scrollerStyle() {
      return {
        [this.isVertical ? 'margin-right' : 'margin-bottom']:
          -this.scrollbarSize + 'px'
      };
    },
    thumbMaxPos() {
      // .vue-recycle-scrollerにネガティブマージンをかけているので-1している
      return this.offsetSize - this.thumbSize - 1;
    },
    thumbPos() {
      let thumbPos =
        this.thumbMaxPos *
        (this.scrollPos / (this.scrollSize - this.offsetSize));
      if (thumbPos < 0) {
        thumbPos = 0;
      } else if (thumbPos > this.thumbMaxPos) {
        thumbPos = this.thumbMaxPos;
      }
      return Math.round(thumbPos);
    },
    thumbSize() {
      let thumbSize = this.offsetSize * (this.offsetSize / this.scrollSize);
      if (thumbSize < this.thumbMinSize) {
        thumbSize = this.thumbMinSize;
      } else if (thumbSize > this.offsetSize) {
        thumbSize = this.offsetSize;
      }
      return Math.round(thumbSize);
    },
    thumbStyle() {
      return {
        [this.isVertical ? 'height' : 'width']: this.thumbSize + 'px',
        [this.isVertical ? 'top' : 'left']: this.thumbPos + 'px'
      };
    },
    hasScrollbar() {
      return this.scrollSize > this.offsetSize;
    },
    isVertical() {
      return this.direction === 'vertical';
    }
  },
  watch: {
    items: {
      handler() {
        this.$nextTick(() => {
          this.resize();
        });
      }
    }
  },
  mounted() {
    this.$refs.scroller.$el.addEventListener('scroll', this.scroll);
  },
  methods: {
    clickScrollbar(e) {
      // クリックした位置までスクロールさせる
      const clientPos = this.isVertical ? e.clientY : e.clientX;
      const scrollPos = this.getScrollPos(clientPos - this.thumbSize / 2);
      this.scrollTo(scrollPos);
      // リストの空白地帯がたまに見える不具合があり、handleResizeを実行したら発生しなくなった
      this.$refs.scroller.handleResize();
    },
    getScrollPos(clientPos) {
      let pos = this.isVertical
        ? clientPos - this.$refs.content.getBoundingClientRect().top
        : clientPos - this.$refs.content.getBoundingClientRect().left;
      if (pos < 0) {
        pos = 0;
      } else if (pos > this.scrollSize) {
        pos = this.scrollSize;
      }
      return Math.round(this.scrollSize * (pos / this.offsetSize));
    },
    mousedown(e) {
      this.isDragging = true;
      this.thumbOffsetSize = this.isVertical ? e.offsetY : e.offsetX;
      // スクロールバーのつまみにmousedownした時にドラッグで動かせるようにする
      // 負荷軽減のためこの時だけmousemoveとmouseupイベントを登録、スクローラーのスクロールイベントを一時的にやめる
      document.body.addEventListener('mousemove', this.mousemove);
      document.body.addEventListener('mouseup', this.mouseup);
      this.$refs.scroller.$el.removeEventListener('scroll', this.scroll);
    },
    mousemove(e) {
      e.preventDefault();
      // スクロールバーの移動とリストの移動を連動させる
      let clientPos = this.isVertical ? e.clientY : e.clientX;
      clientPos -= this.thumbOffsetSize;
      const scrollPos = this.getScrollPos(clientPos);
      this.scrollTo(scrollPos);
    },
    mouseup() {
      this.isDragging = false;
      document.body.removeEventListener('mousemove', this.mousemove);
      document.body.removeEventListener('mouseup', this.mouseup);
      this.$refs.scroller.$el.addEventListener('scroll', this.scroll);
    },
    resize() {
      const $el = this.$refs.scroller.$el;
      if (this.isVertical) {
        this.scrollPos = $el.scrollTop;
        this.offsetSize = $el.offsetHeight;
        this.scrollSize = $el.scrollHeight;
        this.scrollbarSize = $el.offsetWidth - $el.clientWidth;
      } else {
        this.scrollPos = $el.scrollLeft;
        this.offsetSize = $el.offsetWidth;
        this.scrollSize = $el.scrollWidth;
        this.scrollbarSize = $el.offsetHeight - $el.clientHeight;
      }
    },
    scroll() {
      this.scrollPos = this.isVertical
        ? this.$refs.scroller.$el.scrollTop
        : this.$refs.scroller.$el.scrollLeft;
    },
    scrollTo(scrollPos) {
      this.scrollPos = scrollPos;
      this.$refs.scroller.$el[
        this.isVertical ? 'scrollTop' : 'scrollLeft'
      ] = scrollPos;
      this.$refs.scroller.updateVisibleItems(false);
    },
    scrollToEnd() {
      this.$nextTick(() => {
        this.scrollTo(this.scrollSize);
      });
    },
    scrollToStart() {
      this.$nextTick(() => {
        this.scrollTo(0);
      });
    }
  }
};
</script>
